import type { Me, TrendCard } from '@trendator/shared';
import { useEffect, useState, type ComponentType } from 'react';
import { api } from '../api';
import type { Tab } from '../components/TabBar';
import {
  Chevron,
  EnhanceIcon,
  Headset,
  MusicIcon,
  PhotoIcon,
  Play,
  Plus,
  ShirtIcon,
  TrendUp,
  VideoIcon,
} from '../components/icons';
import { rub } from '../format';
import { TrendThumb, trendPrice } from './Catalog';
import { haptic } from '../telegram';

const TOOLS: { label: string; Icon: ComponentType }[] = [
  { label: 'Фото', Icon: PhotoIcon },
  { label: 'Видео', Icon: VideoIcon },
  { label: 'Примерка', Icon: ShirtIcon },
  { label: 'Улучшить', Icon: EnhanceIcon },
  { label: 'Песня', Icon: MusicIcon },
];

const SEGMENTS = ['Все', 'Растут', 'Новые', 'Видео', 'Подарки'] as const;
type Segment = (typeof SEGMENTS)[number];

function bySegment(items: TrendCard[], s: Segment): TrendCard[] {
  switch (s) {
    case 'Растут':
      return [...items].filter((t) => t.popularity > 0).sort((a, b) => b.popularity - a.popularity);
    case 'Новые':
      return items.filter((t) => t.tags.includes('new'));
    case 'Видео':
      return items.filter((t) => t.kind === 'video');
    case 'Подарки':
      return items.filter((t) => t.section === 'gifts');
    default:
      return items;
  }
}

/** Главная — docs/design/Main.dc.html. */
export function Main({
  me,
  onNavigate,
  onTopUp,
  onOpenTrend,
  onCatalog,
}: {
  me: Me;
  onNavigate: (tab: Tab) => void;
  onTopUp: () => void;
  onOpenTrend: (t: TrendCard, rank: number) => void;
  onCatalog: () => void;
}) {
  const [segment, setSegment] = useState<Segment>('Все');
  const [trends, setTrends] = useState<TrendCard[] | null>(null);
  useEffect(() => {
    api
      .trends()
      .then(setTrends)
      .catch(() => setTrends([]));
  }, []);
  // Место в топе — по популярности за 7 дней.
  const ranked = trends ? [...trends].sort((a, b) => b.popularity - a.popularity) : [];
  const shown = bySegment(trends ?? [], segment).slice(0, 6);
  const soon = () => {
    haptic();
    onNavigate('create');
  };

  return (
    <>
      <header className="main__header">
        <span className="main__brand">Trendator</span>
        <div className="main__actions">
          <button
            type="button"
            className="glass balance-pill"
            aria-label={`Баланс ${rub(me.balance)}, пополнить`}
            onClick={() => {
              haptic();
              onTopUp();
            }}
          >
            {rub(me.balance)}
            <span className="balance-pill__plus">
              <Plus />
            </span>
          </button>
          <button type="button" className="glass icon-btn" aria-label="Поддержка" onClick={() => onNavigate('profile')}>
            <Headset />
          </button>
        </div>
      </header>

      <button type="button" className="twin-banner" onClick={() => onNavigate('twin')}>
        <span className="glass-chip">Новое · Двойник</span>
        <span className="twin-banner__bar">
          <span className="twin-banner__text">
            <span className="twin-banner__title">Все тренды с твоим лицом</span>
            <span className="twin-banner__sub">10 фото один раз — и больше ничего не грузишь</span>
          </span>
          <span className="twin-banner__go">
            <Chevron />
          </span>
        </span>
      </button>

      <nav className="tools" aria-label="Инструменты">
        {TOOLS.map(({ label, Icon }) => (
          <button key={label} type="button" className="tool" onClick={soon}>
            <span className="tool__icon">
              <Icon />
            </span>
            {label}
          </button>
        ))}
      </nav>

      <section className="trends">
        <h2 className="trends__title">Тренды сегодня</h2>
        <div className="segments" role="group" aria-label="Фильтр трендов">
          {SEGMENTS.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={segment === s}
              onClick={() => {
                haptic('select');
                setSegment(s);
              }}
            >
              {s}
            </button>
          ))}
        </div>

        {trends && trends.length === 0 ? (
          <div className="glass-list trends__empty">
            <span>Скоро здесь будут готовые тренды. А пока — сделай фото по своему описанию.</span>
            <button type="button" className="link-btn" onClick={() => onNavigate('create')}>
              Создать фото →
            </button>
          </div>
        ) : null}
        {trends && trends.length > 0 && shown.length === 0 ? <p className="trends__none">В этой подборке пока пусто</p> : null}

        <div className="trend-grid">
          {shown.map((t) => {
            const rank = ranked.findIndex((r) => r.id === t.id);
            const growing = t.popularity > 0 && rank < 3;
            return (
              <button
                key={t.id}
                type="button"
                className="trend-card"
                aria-label={t.title}
                onClick={() => {
                  haptic();
                  onOpenTrend(t, rank);
                }}
              >
                <TrendThumb t={t} className="trend-card__bg" />
                <span className={`glass-chip trend-card__rank${rank === 0 && growing ? ' trend-card__rank--top' : ''}`}>#{rank + 1}</span>
                {t.kind === 'video' ? (
                  <span className="glass-chip trend-card__badge" style={{ width: 28, padding: 0, justifyContent: 'center' }}>
                    <Play />
                  </span>
                ) : t.tags.includes('new') ? (
                  <span className="glass-chip trend-card__badge trend-card__badge--new">Новинка</span>
                ) : growing || t.tags.includes('top') ? (
                  <span className="glass-chip trend-card__badge">
                    <TrendUp />
                    растёт
                  </span>
                ) : null}
                <span className="trend-card__info">
                  <span className="trend-card__name">{t.title}</span>
                  <span className="trend-card__meta">
                    <span className="trend-card__soon">{trendPrice(t, me)}</span>
                    <span className="trend-card__sub">{t.subtitle || `${t.frames} фото`}</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {trends && trends.length > 0 ? (
          <button type="button" className="btn-glass trends__all" onClick={onCatalog}>
            Все тренды · {trends.length}
          </button>
        ) : null}
      </section>
    </>
  );
}
